import React, { useMemo, useState, useEffect } from 'react'
import Navigation from './navigation'
import FeatureCarousel from '../paywall/feature-carousel'
import StatsSummaryCards from '../paywall/stats-summary-cards'
import { useLocation, useNavigate } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { useAuth } from '../../hooks/use-auth'
import { PLAN_CONSTANTS } from '../../types/auth'
// Removed generic PlanCard in favor of custom inline design for better CRO on paywall
import Button from '../ui/button'
import { ArrowRight, Check, Star, ExternalLink, Loader2 } from 'lucide-react'
import { getRecentChangelogTotal } from '../../lib/changelog'
import { creditsForMinutes } from '../../lib/plans'
import { startCheckoutSession } from '../../lib/start-checkout-session'
import { PLANS, type PlanId } from '../../lib/plans'
import { track } from '../../lib/analytics/posthog'
import { useNavCollapsed } from '../../lib/nav-collapsed'

interface LayoutProps {
  children: React.ReactNode
}

// High-converting paywall shown to free users.
// Structure: Intro copy (founder tone) + 2-column grid (benefits left, plan card right) + reassurance footer.
const Paywall: React.FC = () => {
  const navigate = useNavigate()
  const featured = PLANS.brew
  const { user } = useAuth()
  const currentPlan: PlanId = (user?.plan as PlanId) || 'free'
  const [loading, setLoading] = useState(false)
  const [couponCode, setCouponCode] = useState<string | null>(null)
  // Use total changelog entries in last 30 days (ignores whether user has seen them)
  const recentFeaturesCount = useMemo(() => getRecentChangelogTotal(30), [])

  // Capture optional coupon from query params (shared logic with billing page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const coupon = params.get('coupon') || params.get('promo') || params.get('code')
    if (coupon) setCouponCode(coupon)
  }, [])

  const handleUpgrade = async () => {
    // Free -> paid goes straight to checkout
    if (currentPlan === 'free') {
      try {
        setLoading(true)
        track('paywall_checkout_clicked', { plan: 'brew', fromPlan: currentPlan })
        await startCheckoutSession('brew', { fromPlan: currentPlan, couponCode, trial: true })
      } catch (e) {
        console.error(e)
        alert('Unable to start checkout. Please try again.')
        setLoading(false)
      }
      return
    }
    // Already on paid plan, send to billing for management
    navigate('/billing')
  }

  return (
    <section className="mx-auto max-w-6xl py-8 sm:py-10 md:py-12 px-4 sm:px-6">
      {/* Mobile and tablet intro heading shown above plan card */}
      <div className="lg:hidden mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">Enjoyed your first 10 minutes?</h1>
        <p className="mt-3 text-sm sm:text-base text-foreground/70 leading-relaxed">
          I'm building this solo. The free trial is on me, but the AI bills are real. Upgrading lets me keep the tool sustainable, fast, and constantly improving.
        </p>
        <FeatureCarousel />
      </div>
      <div className="grid gap-5 sm:gap-6 lg:gap-7 lg:grid-cols-3">
        {/* Left column: intro + benefits */}
        <div className="space-y-4 md:space-y-6 lg:space-y-8 lg:order-1 order-2 lg:col-span-2">
          <div className="max-w-3xl">
            {/* Desktop heading (hidden on mobile and tablet) */}
            <h1 className="hidden lg:block text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-foreground">Enjoyed your first 10 minutes?</h1>
            <p className="hidden lg:block mt-4 text-base md:text-lg text-foreground/70 leading-relaxed">
              I'm building this solo. The free trial is on me, but the AI bills are real. Upgrading lets me keep the tool sustainable, fast, and constantly improving.
            </p>
            <div className="hidden lg:block">
              <FeatureCarousel />
              <StatsSummaryCards recentFeaturesCount={recentFeaturesCount} />
            </div>
            {/* Removed exploration paragraph (paywall always shown for free users) */}
          </div>
          {/* Reviews 2x2 grid */}
          <div className="grid gap-3 sm:gap-4 md:gap-5 lg:gap-6 grid-cols-1 sm:grid-cols-2">
            {/* Review 1 (Reddit) */}
            <div className="group relative rounded-2xl border border-black/10 bg-white/70 backdrop-blur p-4 sm:p-5 shadow-sm shadow-black/5 flex flex-col gap-2 sm:gap-3 min-h-[160px] sm:min-h-[180px]">
              <div className="flex items-center gap-1" aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-warning-600 fill-warning-600" />
                ))}
              </div>
              <p className="text-sm sm:text-base md:text-sm text-foreground leading-relaxed flex-1">
                <span className="font-medium">This is awesome!!!</span> Not only is this the most in-depth AI-podcast generator I've encountered, but it told you what it was doing and allowed you to customize everything.
              </p>
              <div className="flex items-center justify-between text-xs text-foreground/60 mt-auto">
                <span className="font-medium">u/legitimate-draw-548</span>
                <a
                  href="https://www.reddit.com/r/studytips/comments/1n05fb4/comment/narolzb/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-foreground/50 hover:text-foreground transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
            {/* Review 2 */}
            <div className="group relative rounded-2xl border border-black/10 bg-white/70 backdrop-blur p-4 sm:p-5 shadow-sm shadow-black/5 flex flex-col gap-2 sm:gap-3 min-h-[160px] sm:min-h-[180px]">
              <div className="flex items-center gap-1" aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-warning-600 fill-warning-600" />
                ))}
              </div>
              <p className="text-sm sm:text-base md:text-sm text-foreground leading-relaxed flex-1">
                I uploaded a dense 50-page PDF for my exam, and the new series flow turned it into a week of daily 10-minute episodes. It felt like having a personal tutor on my commute.
              </p>
              <div className="flex items-center justify-between text-xs text-foreground/60 mt-auto">
                <span className="font-medium">Maya K.</span>
                <span className="text-foreground/50">✅ Verified purchase</span>
              </div>
            </div>
            {/* Review 3 */}
            <div className="group relative rounded-2xl border border-black/10 bg-white/70 backdrop-blur p-4 sm:p-5 shadow-sm shadow-black/5 flex flex-col gap-2 sm:gap-3 min-h-[160px] sm:min-h-[180px]">
              <div className="flex items-center gap-1" aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-warning-600 fill-warning-600" />
                ))}
              </div>
              <p className="text-sm sm:text-base md:text-sm text-foreground leading-relaxed flex-1">
                Every week, I feed all my newsletters into this and get a single 'weekly digest' episode. It saves me time and helps me understand the content better.
              </p>
              <div className="flex items-center justify-between text-xs text-foreground/60 mt-auto">
                <span className="font-medium">Chris D.</span>
                <span className="text-foreground/50">✅ Verified purchase</span>
              </div>
            </div>
            {/* Review 4 */}
            <div className="group relative rounded-2xl border border-black/10 bg-white/70 backdrop-blur p-4 sm:p-5 shadow-sm shadow-black/5 flex flex-col gap-2 sm:gap-3 min-h-[160px] sm:min-h-[180px]">
              <div className="flex items-center gap-1" aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-warning-600 fill-warning-600" />
                ))}
              </div>
              <p className="text-sm sm:text-base md:text-sm text-foreground leading-relaxed flex-1">
                I'm a huge audiobook fan, and this is like having a personal narrator for any article, blog post, or even my own notes. The voices are surprisingly natural.
              </p>
              <div className="flex items-center justify-between text-xs text-foreground/60 mt-auto">
                <span className="font-medium">Lena P.</span>
                <span className="text-foreground/50">✅ Verified purchase</span>
              </div>
            </div>
          </div>
          {/* Removed social proof + unlock benefits section per request */}
        </div>
        {/* Right column: custom sticky vertically centered plan card */}
        <div className="flex flex-col relative lg:order-2 order-1 mb-6 lg:mb-0 lg:col-span-1">
          {/* Use sticky with a top offset for reliable visibility; fixed translate approach removed */}
          <div className="lg:sticky lg:top-28">
            <div className="rounded-3xl border border-black/10 bg-white/95 backdrop-blur-sm shadow-xl shadow-black/10 p-5 sm:p-6 lg:p-7 flex flex-col gap-5 w-full lg:max-w-sm lg:min-w-[330px] lg:mx-auto">
              {/* Header section - full width on mobile/tablet, compact on desktop */}
              <div className="flex flex-col sm:flex-row lg:flex-col items-start sm:items-center lg:items-start justify-between gap-3 sm:gap-4 lg:gap-2">
                <div className="space-y-1 sm:space-y-2">
                  <p className="text-xs uppercase tracking-wider font-semibold text-brand-600">Brew Plan · $1 first week</p>
                  <h3 className="text-3xl font-semibold text-foreground flex items-end gap-1">
                    <span>{featured.priceLabel}</span>
                    <span className="text-sm font-medium text-foreground/60 mb-1">/wk</span>
                  </h3>
                </div>
                <p className="text-sm text-foreground/60 sm:text-right lg:text-left"><span className="font-medium text-foreground">{featured.episodesPerCycle} episodes per week</span>, any length 10–30 minutes, deep research included.</p>
              </div>

              {/* Features in 2-column grid on mobile/tablet, single column on desktop */}
              <ul className="grid grid-cols-2 lg:grid-cols-1 gap-x-4 gap-y-2.5 lg:gap-y-3 text-sm">
                <li className="flex gap-2">
                  <Check className="h-4 w-4 text-brand-600 shrink-0 mt-0.5" /> Best & good voice modes
                </li>
                <li className="flex gap-2">
                  <Check className="h-4 w-4 text-brand-600 shrink-0 mt-0.5" /> Private RSS feed
                </li>
                <li className="flex gap-2">
                  <Check className="h-4 w-4 text-brand-600 shrink-0 mt-0.5" /> Full-quality MP3 downloads
                </li>
                <li className="flex gap-2">
                  <Check className="h-4 w-4 text-brand-600 shrink-0 mt-0.5" /> Priority improvements & fixes
                </li>
                <li className="flex gap-2 col-span-2 lg:col-span-1">
                  <Check className="h-4 w-4 text-brand-600 shrink-0 mt-0.5" /> Cancel anytime, 7-day guarantee
                </li>
              </ul>

              <Button size="lg" onClick={handleUpgrade} disabled={loading} className="w-full shadow-lg shadow-brand-500/20 hover:shadow-brand-500/30 text-left">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Start $1 first week <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>

              <div className="hidden lg:block text-[11px] leading-relaxed text-foreground/50 text-left">
                Need more minutes later? You can move up or down instantly inside billing. Your episodes + settings stay intact.
              </div>

              <div className="text-center">
                <Button variant="ghost" size="sm" onClick={() => navigate('/billing')} className="text-foreground/60 hover:text-foreground">
                  View all plans
                </Button>
              </div>
            </div>
            {/* Disclaimer moved inside sticky container, below card */}
            <p className="mt-3 text-[11px] text-center text-foreground/40 max-w-sm mx-auto leading-snug">
              By subscribing, you agree to our{' '}
              <a
                href="https://podcast-generator.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground/60 transition-colors"
              >
                Privacy Policy
              </a>{' '}and{' '}
              <a
                href="https://podcast-generator.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground/60 transition-colors"
              >
                Terms of Service
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation()
  const isNavCollapsed = useNavCollapsed()

  // Only the podcast editor uses the edge-to-edge layout shell.
  const isFullWidthEditorPage = useMemo(() => {
    return /^\/podcasts\/[^/]+$/.test(location.pathname)
  }, [location.pathname])

  const { user } = useAuth()
  // Free trial: 10 minutes one-time for free users
  const FREE_TRIAL_MINUTES = 10
  const FREE_TRIAL_CREDITS = creditsForMinutes(FREE_TRIAL_MINUTES)
  const isFreePlan = user?.plan === PLAN_CONSTANTS.FREE_PLAN
  const usedCredits = user?.creditsUsed ?? 0
  const trialConsumed = isFreePlan && usedCredits >= FREE_TRIAL_CREDITS
  // Paywall shows only after trial consumed (and not on billing page)
  const showPaywall = false && isFreePlan && trialConsumed && location.pathname !== '/billing'

  return (
    <div className="min-h-screen bg-background">
      {/* Spacers to avoid overlap with fixed elements */}
      <Navigation />
      <main className={cn(
        "min-h-screen transition-all duration-300",
        isNavCollapsed ? "md:ml-16" : "md:ml-64"
      )}>
        <div className={cn(!isFullWidthEditorPage && 'p-4 sm:p-6 md:p-8 pt-16 md:pt-8 min-h-screen')}>
          {showPaywall ? <Paywall /> : children}
        </div>
      </main>
    </div>
  )
}

export default Layout
